/*
  RYNOR sharp images  (assets/rynor-sharp-images.js, Oct 2026)
  ------------------------------------------------------------------
  Keeps every product and banner photo sharp at whatever size it is drawn: bigger windows,
  browser zoom (Cmd/Ctrl +), rotating a tablet, or any layout the theme's "sizes" hints don't
  describe exactly. Loaded from layout/theme.liquid (deferred, never blocks the page).

  How it works
  - Every photo carries a list of copies (srcset: 180px up to its full width) and a hint (sizes)
    saying how wide it is drawn; the browser downloads the one copy that fits the screen, and
    picks again by itself when the window is resized or zoomed.
  - This script measures how big each photo is really drawn (including the extra enlargement when
    a photo is cropped to fill its box) and works out what the theme's hint says for the current
    screen. While the hint is right it leaves the photo alone. When the hint is too small, it gives
    the browser the real width so it swaps in a sharper copy from the same list. So: the bigger a
    photo gets on screen, the sharper the copy it shows.
  - It never asks for a smaller copy, never touches a photo that is already sharp, waits for
    resizing/zooming to settle, and only handles photos on Shopify's image CDN.
  - Photos that haven't loaded yet (lazy loading) get their real width before they load, so they
    arrive at the right size the first time, with no extra download.

  Turn it off for one photo, or everything inside an element, with data-rynor-sharp="off".
*/
(function () {
  'use strict';

  if (window.RynorSharpImages) return;
  if (typeof window.ResizeObserver !== 'function' || typeof window.WeakMap !== 'function' ||
      typeof window.Set !== 'function' || typeof window.Map !== 'function') return;

  var CDN = /\/cdn\/shop\/|cdn\.shopify\.com\//;
  var ENOUGH = 0.92;     // within 8% of the needed width already looks sharp
  var SETTLE_MS = 200;   // wait this long after the last resize/zoom before acting
  var MIN_BOX = 24;      // ignore tiny images (icons, swatches) and hidden ones

  var states = new WeakMap();
  var tracked = new Set();
  var pending = new Set();
  var hintCache = new Map();
  var settleTimer = 0;
  var resizeObserver = null;
  var probe = null;

  /* ---------- reading the photo ---------- */

  function optedOut(img) {
    return !!(img.closest && img.closest('[data-rynor-sharp="off"]'));
  }

  // The element whose srcset/sizes the browser is using: the img, or the matching <source> in a <picture>.
  function activeSource(img) {
    var picture = img.parentElement;
    if (picture && picture.tagName === 'PICTURE') {
      var sources = picture.getElementsByTagName('source');
      for (var i = 0; i < sources.length; i++) {
        var source = sources[i];
        if (!source.getAttribute('srcset')) continue;
        var media = source.getAttribute('media');
        if (!media || window.matchMedia(media).matches) return source;
      }
    }
    return img.getAttribute('srcset') ? img : null;
  }

  // "url 360w, url 720w, ..." -> [{ href, w }], or null when it isn't a width list on Shopify's CDN.
  function readSrcset(srcset) {
    if (!srcset || !CDN.test(srcset)) return null;
    var list = [];
    var parts = srcset.split(',');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (!part) continue;
      var match = /^(\S+)\s+(\d+)w$/.exec(part);
      if (!match) return null;
      var href;
      try { href = new URL(match[1], document.baseURI).href; } catch (err) { continue; }
      list.push({ href: href, w: parseInt(match[2], 10) });
    }
    return list.length ? list : null;
  }

  // Shape of the photo itself (width / height), from its width/height attributes or the loaded file.
  function photoRatio(img) {
    var w = parseFloat(img.getAttribute('width'));
    var h = parseFloat(img.getAttribute('height'));
    if (w > 0 && h > 0) return w / h;
    if (img.naturalWidth && img.naturalHeight) return img.naturalWidth / img.naturalHeight;
    return 0;
  }

  // Width (CSS px) at which the photo is really painted inside its box.
  function drawnWidth(img, boxW, boxH) {
    var ratio = photoRatio(img);
    if (!ratio || !boxH) return boxW;
    var fit = window.getComputedStyle(img).objectFit;
    if (fit === 'cover') return Math.max(boxW, boxH * ratio);
    if (fit === 'contain' || fit === 'scale-down') return Math.min(boxW, boxH * ratio);
    return boxW;
  }

  /* ---------- what the theme's "sizes" hint says right now ---------- */

  // Splits on commas that aren't inside brackets, e.g. inside calc(), max(), clamp().
  function splitEntries(text) {
    var out = [];
    var depth = 0;
    var start = 0;
    for (var i = 0; i < text.length; i++) {
      var c = text.charAt(i);
      if (c === '(') depth++;
      else if (c === ')') depth = Math.max(0, depth - 1);
      else if (c === ',' && depth === 0) {
        out.push(text.slice(start, i));
        start = i + 1;
      }
    }
    out.push(text.slice(start));
    return out;
  }

  // Index of the last space that isn't inside brackets (separates the condition from the width).
  function lastOuterSpace(text) {
    var depth = 0;
    for (var i = text.length - 1; i >= 0; i--) {
      var c = text.charAt(i);
      if (c === ')') depth++;
      else if (c === '(') depth = Math.max(0, depth - 1);
      else if (depth === 0 && /\s/.test(c)) return i;
    }
    return -1;
  }

  // A CSS length (20vw, calc(...), max(...)) in px for the current window; -1 if not understood.
  function lengthPx(length) {
    if (/^0(px)?$/.test(length)) return 0;
    if (length.indexOf('%') !== -1) return -1; // percentages aren't allowed in sizes; browsers skip them
    if (!probe) {
      probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText = 'position:absolute;top:0;left:-10000px;height:0;overflow:hidden;visibility:hidden;pointer-events:none;';
      (document.body || document.documentElement).appendChild(probe);
    }
    probe.style.width = '';
    probe.style.width = length;
    if (!probe.style.width) return -1;
    return probe.getBoundingClientRect().width;
  }

  // The width the theme's sizes hint gives for this window, like the browser works it out.
  function hintedWidth(source) {
    var attr = (source.getAttribute('sizes') || '').trim();
    var key = attr + '|' + window.innerWidth + 'x' + window.innerHeight;
    if (hintCache.has(key)) return hintCache.get(key);

    var result = window.innerWidth; // no usable hint = 100vw
    var entries = attr ? splitEntries(attr) : [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i].trim();
      if (!entry) continue;
      var cut = lastOuterSpace(entry);
      var length = cut < 0 ? entry : entry.slice(cut + 1);
      var condition = cut < 0 ? '' : entry.slice(0, cut).trim();
      if (length === 'auto') continue;
      var px = lengthPx(length);
      if (px < 0) continue;
      if (condition && !window.matchMedia(condition).matches) continue;
      result = px;
      break;
    }

    if (hintCache.size > 200) hintCache.clear();
    hintCache.set(key, result);
    return result;
  }

  /* ---------- deciding ---------- */

  function pin(source, state, px) {
    state.asked.set(source, px);
    source.setAttribute('sizes', px + 'px');
  }

  function check(img) {
    var state = states.get(img);
    if (!state) return;

    if (!img.isConnected) {
      forget(img);
      return;
    }
    if (state.w < MIN_BOX || state.h < 1) return; // hidden or tiny

    var source = activeSource(img);
    if (!source) return;
    var list = readSrcset(source.getAttribute('srcset'));
    if (!list) return;

    var drawn = drawnWidth(img, state.w, state.h);
    var px = Math.ceil(drawn);

    // Already gave the browser this photo's real width: keep it in step as the photo grows.
    var pinned = state.asked.get(source) || 0;
    if (pinned) {
      if (px > pinned) pin(source, state, px);
      return;
    }

    var needed = drawn * (window.devicePixelRatio || 1);
    var biggest = 0;
    var current = null;
    var showing = img.currentSrc || '';
    for (var i = 0; i < list.length; i++) {
      if (list[i].w > biggest) biggest = list[i].w;
      if (showing && list[i].href === showing) current = list[i].w;
    }
    var sharp = current !== null && current >= Math.min(needed, biggest) * ENOUGH;

    // The theme's hint is right for this screen, so the browser keeps the photo sharp by itself
    // (also when the page is zoomed or resized)...
    if (hintedWidth(source) >= drawn * ENOUGH) {
      // ...unless it hasn't picked again since the page changed size: nudge it once.
      if (current !== null && !sharp && state.nudged !== px) {
        state.nudged = px;
        source.setAttribute('sizes', source.getAttribute('sizes') || '100vw');
      }
      return;
    }

    // The hint is too small for how big this photo really is.
    if (sharp) return; // the copy on screen is sharp enough anyway
    pin(source, state, px);
  }

  function flush() {
    settleTimer = 0;
    var batch = Array.from(pending);
    pending.clear();
    for (var i = 0; i < batch.length; i++) check(batch[i]);
  }

  function queue(img) {
    pending.add(img);
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(flush, SETTLE_MS);
  }

  /* ---------- watching ---------- */

  function onResize(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var img = entry.target;
      var state = states.get(img);
      if (!state) continue;

      var box = entry.contentBoxSize && entry.contentBoxSize[0];
      state.w = box ? box.inlineSize : entry.contentRect.width;
      state.h = box ? box.blockSize : entry.contentRect.height;

      if (!state.measured) {
        if (state.w < MIN_BOX) continue; // still hidden: wait for a real size
        // First real size: a photo that hasn't loaded yet gets its real width right away,
        // before the browser starts downloading it.
        state.measured = true;
        check(img);
      } else {
        queue(img);
      }
    }
  }

  function track(img) {
    if (states.has(img) || optedOut(img)) return;
    var source = activeSource(img);
    if (!source || !CDN.test(source.getAttribute('srcset') || '')) return;

    states.set(img, { w: 0, h: 0, measured: false, nudged: 0, asked: new WeakMap() });
    tracked.add(img);
    resizeObserver.observe(img);
  }

  function forget(img) {
    states.delete(img);
    tracked.delete(img);
    pending.delete(img);
    try { resizeObserver.unobserve(img); } catch (err) { /* ignore */ }
  }

  function eachImg(root, fn) {
    if (!root || root.nodeType !== 1) return;
    if (root.tagName === 'IMG') {
      fn(root);
      return;
    }
    if (!root.getElementsByTagName) return;
    var imgs = root.getElementsByTagName('img');
    for (var i = 0; i < imgs.length; i++) fn(imgs[i]);
  }

  function recheckAll() {
    tracked.forEach(function (img) { queue(img); });
  }

  // Browser zoom and moving the window to another screen change the pixel density without
  // always resizing anything, so listen for that too.
  function watchDensity() {
    if (!window.matchMedia) return;
    var query = window.matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)');
    var onChange = function () {
      if (query.removeEventListener) query.removeEventListener('change', onChange);
      else if (query.removeListener) query.removeListener(onChange);
      recheckAll();
      watchDensity();
    };
    if (query.addEventListener) query.addEventListener('change', onChange);
    else if (query.addListener) query.addListener(onChange);
  }

  function onMutations(records) {
    for (var i = 0; i < records.length; i++) {
      var record = records[i];

      if (record.type === 'attributes') {
        // A script swapped a photo's srcset (variant change, slider, cart...): track it / look again.
        var target = record.target;
        var img = target.tagName === 'SOURCE' && target.parentElement
          ? target.parentElement.getElementsByTagName('img')[0]
          : target;
        if (!img || img.tagName !== 'IMG') continue;
        var state = states.get(img);
        if (state) {
          state.asked = new WeakMap();
          state.nudged = 0;
          queue(img);
        } else {
          track(img);
        }
        continue;
      }

      var added = record.addedNodes;
      for (var j = 0; j < added.length; j++) eachImg(added[j], track);

      var removed = record.removedNodes;
      for (var k = 0; k < removed.length; k++) {
        eachImg(removed[k], function (gone) {
          if (!gone.isConnected && states.has(gone)) forget(gone);
        });
      }
    }
  }

  function init() {
    resizeObserver = new ResizeObserver(onResize);
    eachImg(document.body || document.documentElement, track);

    // Photos added later: load more, recommendations, recently viewed, search, cart, card sliders.
    if (typeof window.MutationObserver === 'function') {
      new MutationObserver(onMutations).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['srcset']
      });
    }

    watchDensity();
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) recheckAll(); // back/forward cache: the window may have changed size
    });
  }

  window.RynorSharpImages = {
    recheck: recheckAll,
    track: function (root) { eachImg(root, track); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
